/**
 * ShareLinkDialog Component
 * Dialog for sharing Kit de Atendimento link
 *
 * @version 2025.1.0
 */

import React, { useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  IconButton,
  InputAdornment,
  Alert,
  Box,
  Typography,
  Tooltip,
} from '@mui/material';
import {
  ContentCopy as CopyIcon,
  Check as CheckIcon,
  Share as ShareIcon,
  WhatsApp as WhatsAppIcon,
  Email as EmailIcon,
} from '@mui/icons-material';

interface ShareLinkDialogProps {
  open: boolean;
  onClose: () => void;
  link: string;
  expiresAt?: Date;
  patientName?: string;
}

export const ShareLinkDialog: React.FC<ShareLinkDialogProps> = ({
  open,
  onClose,
  link,
  expiresAt,
  patientName,
}) => {
  const [copied, setCopied] = useState(false);

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(link);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch (error) {
      console.error('Failed to copy:', error);
    }
  };

  const handleShareWhatsApp = () => {
    const message = encodeURIComponent(
      `Olá${patientName ? ` ${patientName}` : ''}! Segue o link para acessar seu Kit de Atendimento: ${link}`
    );
    window.open(`https://wa.me/?text=${message}`, '_blank');
  };

  const handleShareEmail = () => {
    const subject = encodeURIComponent('Kit de Atendimento - Acesso aos Documentos');
    const body = encodeURIComponent(
      `Olá${patientName ? ` ${patientName}` : ''},\n\nSegue o link para acessar seu Kit de Atendimento:\n${link}\n\nEste link é válido por 30 dias.\n\nAtenciosamente,\nEquipe de Atendimento`
    );
    window.open(`mailto:?subject=${subject}&body=${body}`);
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        <ShareIcon color="primary" />
        Compartilhar Kit de Atendimento
      </DialogTitle>

      <DialogContent>
        {/* Info Alert */}
        <Alert severity="info" sx={{ mb: 3 }}>
          Este link permite acesso aos documentos do paciente por 30 dias.
          O paciente poderá visualizar e baixar a Guia, ASO e Prontuário.
        </Alert>

        {/* Link Field */}
        <TextField
          fullWidth
          value={link}
          label="Link de Acesso"
          InputProps={{
            readOnly: true,
            endAdornment: (
              <InputAdornment position="end">
                <Tooltip title={copied ? 'Copiado!' : 'Copiar link'}>
                  <IconButton onClick={handleCopy} edge="end">
                    {copied ? (
                      <CheckIcon color="success" />
                    ) : (
                      <CopyIcon />
                    )}
                  </IconButton>
                </Tooltip>
              </InputAdornment>
            ),
          }}
          sx={{ mb: 3 }}
        />

        {/* Expiration Info */}
        {expiresAt && (
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Válido até: {expiresAt.toLocaleDateString('pt-BR', {
              day: '2-digit',
              month: 'long',
              year: 'numeric',
            })}
          </Typography>
        )}

        {/* Share Options */}
        <Box sx={{ display: 'flex', gap: 2, justifyContent: 'center', mt: 2 }}>
          <Button
            variant="outlined"
            startIcon={<WhatsAppIcon />}
            onClick={handleShareWhatsApp}
            sx={{
              color: '#25D366',
              borderColor: '#25D366',
              '&:hover': {
                borderColor: '#128C7E',
                backgroundColor: 'rgba(37, 211, 102, 0.1)',
              },
            }}
          >
            WhatsApp
          </Button>
          <Button
            variant="outlined"
            startIcon={<EmailIcon />}
            onClick={handleShareEmail}
          >
            E-mail
          </Button>
        </Box>
      </DialogContent>

      <DialogActions>
        <Button onClick={onClose}>Fechar</Button>
      </DialogActions>
    </Dialog>
  );
};

export default ShareLinkDialog;
